import React from "react";
import { arrayOf, oneOf, shape, string, bool } from "prop-types";
import { Paper, Box } from "@mui/material";
import { useParams, useLocation, Navigate } from "react-router";
import NoAccess from "shared/chrome/NoAccess";

import TabBar from "./TabBar";
import GridFilter from "./menus/GridFilter";
import CsvDownload from "./menus/CsvDownload";
import ComparisonChart from "./menus/ComparisonChart";
import AnalysesTable from "./AnalysesTable";
import ReportSnapshots from "./ReportSnapshots";
import { connectToState } from "../redux/connect-api";

import {
  activeTabKeySelector,
  analysesLoadingSelector,
  filteredAnalysisIdsSelector,
  filteredAnalysisIdsForAllTabsSelector,
} from "../selectors";

import { tabKeys } from "../config";
import { menu } from "../component-styles";
import {
  userAccessStatusSelector,
  userIsTrialSubscriberSelector,
  userProfileLoadingSelector,
} from "../selectors/userProfile";
import LimitedAccessModal from "./LimitedAccessModal";

const TabNavigator = ({
  activeTabKey,
  rowIds,
  rowIdsForExport,
  analysesLoading,
  hasToolAccess,
  subscriptionIsTrial,
  userLoading,
  location,
  params,
}) => {
  // Validate activeTabKey - redirect to /tools if invalid
  const validTabKeys = ["drugs", "compare-your-own-pricing", "icer-pricing", "report-snapshots"];
  if (activeTabKey && !validTabKeys.includes(activeTabKey)) {
    return <Navigate to="/tools" replace />;
  }

  const onDrugList = activeTabKey === "drugs";
  const hasDetailId = params?.detailId != null;
  // Show ReportSnapshots if we're on the report-snapshots tab OR if we're on drugs tab with a detailId (detail view)
  const onReportSnapshots =
    activeTabKey === "report-snapshots" || (activeTabKey === "drugs" && hasDetailId);
  const showGridFilter = !onDrugList && !onReportSnapshots;
  const controls = () => (
    <>
      {showGridFilter ? <GridFilter /> : null}
      <ComparisonChart disabled={analysesLoading} rowIds={rowIds} />
      <Box sx={menu.menuActionsSecondary}>
        <CsvDownload disabled={analysesLoading} rowIds={rowIdsForExport} />
      </Box>
    </>
  );
  const searchTerm = () => {
    if (location.search) {
      return decodeURI(location.search.replace("?search=", "").replace("+", " "));
    } else {
      return "";
    }
  };

  if (!userLoading && !hasToolAccess) {
    return <NoAccess />;
  }

  return (
    <>
      {subscriptionIsTrial && <LimitedAccessModal />}
      <TabBar location={location} params={params} activeTabKey={activeTabKey} />
      <Box
        component="main"
        id={`tabpanel-${activeTabKey}`}
        sx={(theme) =>
          onReportSnapshots
            ? {
                display: "flex",
                flex: "1 1 auto",
                flexDirection: "column",
                minHeight: 0,
                paddingLeft: theme.spacing(4),
                paddingRight: theme.spacing(4),
                paddingTop: 0,
                paddingBottom: 0,
                width: "100%",
                overflow: "hidden",
                "& > div": {
                  flexDirection: "column",
                },
              }
            : {
                display: "flex",
                flex: "1 1 auto",
                flexDirection: "column",
                minHeight: 0,
                paddingLeft: theme.spacing(4),
                paddingRight: theme.spacing(4),
                paddingTop: 0,
                paddingBottom: 0,
                width: "100%",
                overflow: "hidden",
              }
        }
      >
        {!onReportSnapshots && (
          <Paper elevation={0} id="utility_bar" sx={{ ...menu.menu, flex: "0 0 auto" }}>
            {location.search && onDrugList ? (
              <Box component="p" sx={menu.searchString}>
                Search keyword: <i>{searchTerm()}</i>
              </Box>
            ) : null}
            {controls()}
          </Paper>
        )}
        <Paper
          className="analyses-container"
          elevation={0}
          id="main-content"
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            minHeight: 0,
            paddingTop: 0,
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {onReportSnapshots ? (
            <ReportSnapshots
              loading={userLoading}
              searchTerm={searchTerm()}
              location={location}
              params={params}
            />
          ) : (
            <AnalysesTable
              activeTabKey={activeTabKey}
              loading={analysesLoading}
              rowIds={rowIds}
              searchTerm={searchTerm()}
            />
          )}
        </Paper>
      </Box>
    </>
  );
};

TabNavigator.propTypes = {
  activeTabKey: oneOf(tabKeys).isRequired,
  analysesLoading: bool.isRequired,
  rowIds: arrayOf(string).isRequired,
  params: shape({
    activeTabKey: oneOf(tabKeys),
    detailId: string,
    detailsSlug: string,
  }),
  location: shape({
    search: string.isRequired,
  }).isRequired,
  hasToolAccess: bool,
  loading: bool,
  rowIdsForExport: arrayOf(string),
  subscriptionIsTrial: bool,
  userLoading: bool,
};

const mapStateToProps = (state, ownProps) => ({
  activeTabKey: activeTabKeySelector(state, ownProps),
  analysesLoading: analysesLoadingSelector(state, ownProps),
  rowIds: filteredAnalysisIdsSelector(state, ownProps),
  rowIdsForExport: filteredAnalysisIdsForAllTabsSelector(state, ownProps),
  hasToolAccess: userAccessStatusSelector(state),
  subscriptionIsTrial: userIsTrialSubscriberSelector(state),
  userLoading: userProfileLoadingSelector(state),
});

// Connect TabNavigator (not TabNavigatorWithRouter) so that route props are passed as ownProps
const ConnectedTabNavigator = connectToState(mapStateToProps)(TabNavigator);

// Export wrapper that injects router hooks and passes them as props to connected component
const TabNavigatorWithRouterConnected = (props) => {
  const params = useParams();
  const location = useLocation();
  // Pass router props as ownProps so Redux connect sees them as changed
  // Create new objects to ensure reference changes are detected
  return <ConnectedTabNavigator {...props} params={{ ...params }} location={{ ...location }} />;
};

export default TabNavigatorWithRouterConnected;
